/**
 * Phase BACKGROUND-ANALYSIS-P1a.5-vi — regenerateOutfit utility.
 *
 * Creates a NEW outfit row (parent_outfit_id = original) and fires the
 * appropriate orchestrator. Idempotency is status-gated against the parent
 * row so accidental double-fires return 'in_flight' or 'already_complete'
 * instead of spawning duplicate work.
 *
 * Atomic flow:
 *   1. Pre-flight read of parent row (guest RPC or authed direct select)
 *   2. Status gate (complete -> already_complete; pending -> in_flight)
 *   3. INSERT new outfit row with deep-copied identity/scope/input fields
 *   4. Dispatch orchestrator on new outfit_id (fire-and-forget)
 *   5. Return { status: 'dispatched', newOutfitId }
 *
 * Caller (useRetryAnalysis) handles attempt counter + navigation.
 */
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { invokeGenerator, type GeneratorName } from "@/lib/invokeGenerator";


type OutfitRow = Database["public"]["Tables"]["outfits"]["Row"];
type OutfitInsert = Database["public"]["Tables"]["outfits"]["Insert"];

export interface RegenerateOutfitResult {
  status: "dispatched" | "in_flight" | "already_complete" | "error";
  newOutfitId?: string;
  error?: string;
}

/**
 * Map input_method/source_type → orchestrator edge function name.
 * Falls back to "analyse-outfit" (photo orchestrator) for unknown values.
 */
function orchestratorFor(parent: Pick<OutfitRow, "input_method" | "source_type">): string {
  const m = (parent.input_method ?? parent.source_type ?? "photo").toLowerCase();
  if (m === "url") return "analyse-outfit-url";
  if (m === "text") return "analyse-outfit-text";
  if (m === "social") return "analyse-outfit-social";
  // photo, screenshot, share_intent → default orchestrator
  return "analyse-outfit";
}

export async function regenerateOutfit(
  parentOutfitId: string,
): Promise<RegenerateOutfitResult> {
  try {
    // ----- 1. Pre-flight: fetch parent row (RLS-aware) -----
    // Try direct select first (works for authed users). For guests, the
    // get_guest_outfit RPC bypasses RLS via SECURITY DEFINER.
    let parent: OutfitRow | null = null;
    const direct = await supabase
      .from("outfits")
      .select("*")
      .eq("id", parentOutfitId)
      .maybeSingle();

    if (direct.data) {
      parent = direct.data as OutfitRow;
    } else {
      // Fallback: guest path
      const guest = await (supabase.rpc as never as (
        fn: "get_guest_outfit",
        args: { p_id: string },
      ) => Promise<{ data: OutfitRow[] | null; error: unknown }>)(
        "get_guest_outfit",
        { p_id: parentOutfitId },
      );
      if (guest.data && guest.data.length > 0) {
        parent = guest.data[0];
      }
    }

    if (!parent) {
      return { status: "error", error: "Parent outfit not found" };
    }

    // ----- 2. Status gate -----
    const aStatus = parent.analysis_status ?? "pending";
    const lStatus = parent.lookbook_status ?? null;
    if (aStatus === "complete" && lStatus === "complete") {
      return { status: "already_complete" };
    }
    // Anything that isn't terminal (complete | failed) is still running.
    if (aStatus !== "complete" && aStatus !== "failed") {
      return { status: "in_flight" };
    }

    // ----- 3. INSERT new outfit row (deep-copy fields) -----
    const insertRow: OutfitInsert = {
      // identity / scope
      user_id: parent.user_id,
      is_guest: parent.is_guest,
      guest_session_id: parent.guest_session_id,
      is_sample: false,
      is_saved: false,
      // lineage
      parent_outfit_id: parent.id,
      // input fields (deep copy)
      input_method: parent.input_method,
      source_type: parent.source_type,
      source_url: parent.source_url,
      source_platform: parent.source_platform,
      source_author: parent.source_author,
      source_caption: parent.source_caption,
      image_path: parent.image_path,
      image_paths: parent.image_paths,
      image_url: parent.image_url,
      text_description: parent.text_description,
      occasion: parent.occasion,
      notes: parent.notes,
      outfit_type: parent.outfit_type,
      analysis_mode: parent.analysis_mode,
      selected_wardrobe_items: parent.selected_wardrobe_items,
      user_intent_retailer: parent.user_intent_retailer,
      detected_gender: parent.detected_gender,
      effective_gender: parent.effective_gender,
      // status fields → fresh start
      analysis_status: "pending",
      lookbook_status: null,
      analysis_error: null,
      lookbook_error: null,
      lookbook_lb2_error: null,
      look_ready_email_sent_at: null,
      // hero / LB fields → NULL (will be repopulated by orchestrator)
      hero_item_id: null,
      hero_item_name: null,
      hero_item_category: null,
      hero_item_color_hex: null,
      hero_item_source_crop_url: null,
      hero_item_thumbnail: null,
      thumbnail_url: null,
      lookbook_image_url: null,
      lookbook_image_urls: null,
      lookbook_lb2_url: null,
      lookbook_lb3_url: null,
      style_summary: null,
      outfit_concept: null,
      title: null,
      setting: null,
      garment_continuity: null,
    };

    const ins = await supabase
      .from("outfits")
      .insert(insertRow as never)
      .select("id")
      .single();

    if (ins.error || !ins.data) {
      return {
        status: "error",
        error: ins.error?.message ?? "Failed to create regeneration row",
      };
    }
    const newOutfitId = (ins.data as { id: string }).id;

    // ----- 4. Dispatch orchestrator (fire-and-forget) -----
    const fn = orchestratorFor(parent);
    // Minimal body — orchestrator hydrates the rest from the outfit row.
    const body: Record<string, unknown> = {
      outfit_id: newOutfitId,
      is_guest: parent.is_guest ?? false,
      guest_session_id: parent.guest_session_id ?? null,
    };
    if (fn === "analyse-outfit") {
      body.source_type = parent.source_type ?? "photo";
      body.image_path = parent.image_path;
      body.image_url = parent.image_url;
    } else if (fn === "analyse-outfit-url") {
      body.source_url = parent.source_url;
    } else if (fn === "analyse-outfit-text") {
      body.text_description = parent.text_description;
    } else if (fn === "analyse-outfit-social") {
      body.source_url = parent.source_url;
      body.source_platform = parent.source_platform;
    }

    // Fire-and-forget — do not await. If invoke throws synchronously
    // (rare), surface as dispatched anyway; stuck detection + retry
    // remain the safety net for runtime orchestrator failures.
    void invokeGenerator(fn as GeneratorName, { source: "regenerate", body })
      .then(({ error, quotaExceeded }) => {
        if (quotaExceeded || !error) return;
        void supabase
          .from("outfits")
          .update({
            analysis_status: "failed",
            analysis_error: String(error.message ?? error),
          } as never)
          .eq("id", newOutfitId);
      })
      .catch((e) => {
        void supabase
          .from("outfits")
          .update({
            analysis_status: "failed",
            analysis_error: String(e?.message ?? e),
          } as never)
          .eq("id", newOutfitId);
      });


    return { status: "dispatched", newOutfitId };
  } catch (e) {
    return { status: "error", error: e instanceof Error ? e.message : String(e) };
  }
}
